import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';

interface PatientLimitDropJustificationModalProps {
  open: boolean;
  /** Porcentaje de baja ya calculado (redondeado), ej. 35 para "35%" */
  dropPercentage: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export const PatientLimitDropJustificationModal: React.FC<PatientLimitDropJustificationModalProps> = ({
  open,
  dropPercentage,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    if (submitting) return;
    setReason('');
    onCancel();
  };

  const canConfirm = reason.trim().length > 0 && !submitting;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-md w-full p-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 relative" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
          <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X size={16} />
          </DialogClose>
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ color: '#1e1b4b' }}>
              Antes de confirmar este cambio
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 pb-2 flex flex-col gap-4">
          <p className="text-sm text-gray-700 leading-relaxed m-0">
            Notamos que tu nueva preferencia queda un <strong>{dropPercentage}%</strong> por debajo del límite que tenemos definido para vos.
          </p>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4">
            <p className="text-xs text-amber-700 leading-relaxed m-0">
              Bajar tu tope puede implicar que te asignemos con menor prioridad nuevos pacientes hasta que vuelvas a subirlo.
            </p>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed m-0">
            Nos ayuda mucho entender qué te lleva a este cambio, así podemos seguir dándote el mejor acompañamiento posible. Contanos brevemente el motivo:
          </p>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Escribí el motivo acá..."
            rows={4}
            aria-label="Motivo de la baja en tu preferencia"
            disabled={submitting}
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-col gap-2 mt-4">
          <button
            type="button"
            className="btn-purple w-full"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? 'Guardando...' : 'Confirmar cambio'}
          </button>
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors py-1 disabled:text-gray-300 disabled:cursor-not-allowed"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancelar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientLimitDropJustificationModal;
